import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Nabvar from "../../common/Nabvar";
import Sidebar from "../../common/Sidebar";
import axios from "../../config/authaxios";
import Body from "../../ui/Body";
import DenominationTable from "./DenominationTable";
import {
  buildDenominationRows,
  buildDiscountPayload,
  fetchEligibleVouchers,
  fetchSkuDenominations,
  formatVoucherOptionLabel,
  getCategoryTypeLabel,
  syncDenominationRowForType,
  validateDiscountForm,
} from "./discountUtils";

const defaultForm = {
  publisher_id: "",
  sku: "",
  category_type: null,
  discount_enabled: false,
  discount_type: "upto",
  denominations: [],
  max_discount_cap: 0,
  cashback_percent: "",
};

export default function DiscountForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDeleted = searchParams.get("is_deleted") ?? "false";

  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [publishers, setPublishers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [allowedDenominations, setAllowedDenominations] = useState([]);
  const [skuMeta, setSkuMeta] = useState(null);
  const [loadingDenoms, setLoadingDenoms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const selectedVoucher = useMemo(
    () => vouchers.find((v) => v.sku === form.sku),
    [vouchers, form.sku]
  );

  const loadSkuDenominations = (sku, savedDenominations = []) => {
    if (!sku) {
      setAllowedDenominations([]);
      setSkuMeta(null);
      setForm((prev) => ({ ...prev, denominations: [] }));
      return;
    }

    setLoadingDenoms(true);
    fetchSkuDenominations(axios, sku)
      .then((data) => {
        setSkuMeta(data);
        setAllowedDenominations(data.denominations || []);
        setForm((prev) => ({
          ...prev,
          category_type: data.category_type,
          denominations: buildDenominationRows(data.denominations, savedDenominations),
        }));
      })
      .catch((err) => {
        setAllowedDenominations([]);
        setSkuMeta(null);
        setForm((prev) => ({ ...prev, denominations: [] }));
        setSnackbar({
          open: true,
          message: err.message || "Failed to load SKU denominations",
          severity: "error",
        });
      })
      .finally(() => setLoadingDenoms(false));
  };

  useEffect(() => {
    axios.get("/admin/publishers/name-id").then((res) => {
      if (res.data.status_code === 401) localStorage.clear();
      if (res.data.status) setPublishers(res.data.response || []);
    });
    fetchEligibleVouchers(axios).then(setVouchers);
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    axios
      .get(`/admin/discount/${id}?is_deleted=${isDeleted}`)
      .then((res) => {
        if (res.data.status_code === 401) localStorage.clear();
        if (res.data.status_code === 404) {
          setSnackbar({ open: true, message: "Config not found", severity: "error" });
          navigate("/discounts");
          return;
        }
        if (res.data.status) {
          const d = res.data.response;
          setForm({
            publisher_id: d.publisher_id,
            sku: d.sku,
            category_type: d.category_type,
            discount_enabled: d.discount_enabled,
            discount_type: d.discount_type || "upto",
            denominations: d.denominations || [],
            max_discount_cap: d.max_discount_cap ?? 0,
            cashback_percent: d.cashback_percent ?? "",
          });
          loadSkuDenominations(d.sku, d.denominations || []);
        }
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Failed to load config", severity: "error" });
      })
      .finally(() => setFetching(false));
  }, [id, isEdit, isDeleted, navigate]);

  const updateField = (field, value) => {
    setForm((prev) => {
      let next = { ...prev, [field]: value };
      if (field === "discount_type" && value === "fixed") {
        next.denominations = next.denominations.map((row) =>
          syncDenominationRowForType(row, "fixed")
        );
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSkuChange = (sku) => {
    setForm((prev) => ({ ...prev, sku, denominations: [] }));
    setErrors((prev) => ({ ...prev, sku: undefined, denominations: undefined }));
    loadSkuDenominations(sku);
  };

  const handleDenominationsChange = (denominations) => {
    setForm((prev) => ({ ...prev, denominations }));
    setErrors((prev) => ({ ...prev, denominations: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isEdit) {
      if (!form.publisher_id) {
        setErrors({ publisher_id: "Publisher is required" });
        return;
      }
      if (!form.sku) {
        setErrors({ sku: "SKU is required" });
        return;
      }
    }

    const validationErrors = validateDiscountForm(
      form,
      allowedDenominations,
      selectedVoucher || skuMeta
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    const payload = buildDiscountPayload(form, isEdit);
    const request = isEdit
      ? axios.put(`/admin/discount/${id}`, payload)
      : axios.post("/admin/discount", payload);

    request
      .then((res) => {
        if (res.data.status_code === 401) localStorage.clear();
        if (res.data.status) {
          setSnackbar({
            open: true,
            message: res.data.message || (isEdit ? "Updated successfully" : "Created successfully"),
            severity: "success",
          });
          setTimeout(() => navigate("/discounts"), 1000);
        } else {
          setSnackbar({
            open: true,
            message: res.data.message || "Request failed",
            severity: "error",
          });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Something went wrong";
        setSnackbar({ open: true, message: msg, severity: "error" });
      })
      .finally(() => setLoading(false));
  };

  const publisherName = publishers.find((p) => p._id === form.publisher_id)?.name;
  const categoryLabel = getCategoryTypeLabel(form.category_type || skuMeta?.category_type);
  const discountFieldsDisabled = !form.discount_enabled;

  return (
    <>
      <Sidebar />
      <Nabvar />
      <Body>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/discounts")}>
            Back
          </Button>
          <Typography variant="h5">
            {isEdit ? "Edit Discount Config" : "Add Discount Config"}
          </Typography>
        </Stack>

        <Card sx={{ p: 3, maxWidth: 760 }}>
          {fetching ? (
            <Typography>Loading...</Typography>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {isEdit ? (
                  <>
                    <TextField
                      label="Publisher"
                      value={publisherName || form.publisher_id}
                      disabled
                      fullWidth
                    />
                    <TextField label="SKU" value={form.sku} disabled fullWidth />
                    <TextField label="Category" value={categoryLabel} disabled fullWidth />
                  </>
                ) : (
                  <>
                    <FormControl fullWidth error={Boolean(errors.publisher_id)}>
                      <InputLabel>Publisher *</InputLabel>
                      <Select
                        value={form.publisher_id}
                        label="Publisher *"
                        onChange={(e) => updateField("publisher_id", e.target.value)}
                      >
                        {publishers.map((p) => (
                          <MenuItem key={p._id} value={p._id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.publisher_id && (
                        <FormHelperText>{errors.publisher_id}</FormHelperText>
                      )}
                    </FormControl>

                    <Autocomplete
                      options={vouchers}
                      getOptionLabel={formatVoucherOptionLabel}
                      value={selectedVoucher || null}
                      onChange={(_, val) => handleSkuChange(val?.sku || "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Voucher SKU *"
                          error={Boolean(errors.sku)}
                          helperText={
                            errors.sku ||
                            "Eligible vouchers: Qwik (1), UPI (4), Hubble (5)"
                          }
                        />
                      )}
                    />
                  </>
                )}

                {skuMeta && (
                  <Alert severity="info">
                    {skuMeta.name} · Category: {categoryLabel}
                    {skuMeta.category_type && selectedVoucher?.c_percentage != null &&
                      ` · Global cashback: ${selectedVoucher.c_percentage}%`}
                  </Alert>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.discount_enabled}
                      onChange={(e) => updateField("discount_enabled", e.target.checked)}
                    />
                  }
                  label="Enable discount"
                />

                <FormControl disabled={discountFieldsDisabled}>
                  <FormLabel>Discount type</FormLabel>
                  <RadioGroup
                    row
                    value={form.discount_type}
                    onChange={(e) => updateField("discount_type", e.target.value)}
                  >
                    <FormControlLabel value="upto" control={<Radio />} label="Upto" />
                    <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
                  </RadioGroup>
                  <FormHelperText>
                    Fixed = same % for base and max; Upto = variable up to max %
                  </FormHelperText>
                </FormControl>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Per-denomination discount (%)
                  </Typography>
                  {errors.denominations && (
                    <FormHelperText error sx={{ mb: 1 }}>
                      {errors.denominations}
                    </FormHelperText>
                  )}
                  <DenominationTable
                    rows={form.denominations}
                    discountType={form.discount_type}
                    errors={errors}
                    loading={loadingDenoms}
                    disabled={discountFieldsDisabled}
                    onChange={handleDenominationsChange}
                  />
                </Box>

                <TextField
                  type="number"
                  label="Max discount cap (₹)"
                  value={form.max_discount_cap}
                  onChange={(e) => updateField("max_discount_cap", e.target.value)}
                  error={Boolean(errors.max_discount_cap)}
                  helperText={
                    errors.max_discount_cap || "Optional absolute ₹ cap (0 = no cap)"
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                  disabled={discountFieldsDisabled}
                  fullWidth
                />

                <TextField
                  type="number"
                  label="Cashback override (%)"
                  value={form.cashback_percent}
                  onChange={(e) => updateField("cashback_percent", e.target.value)}
                  error={Boolean(errors.cashback_percent)}
                  helperText={
                    errors.cashback_percent ||
                    "Leave empty to inherit voucher global cashback. Can be combined with per-denomination discount above."
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                  fullWidth
                />

                <Stack direction="row" spacing={2}>
                  <Button type="submit" variant="contained" disabled={loading || loadingDenoms}>
                    {loading ? "Saving..." : isEdit ? "Update" : "Create"}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/discounts")}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Card>
      </Body>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
